// Personnel HR document store. Admin-only via RLS. Every read/download/
// replace/delete writes an audit row to `instructor_document_access_log`.
import { supabase } from "@/integrations/supabase/client";

export type HrDocType =
  | "background_check"
  | "cpr_certification"
  | "teaching_certificate"
  | "contractor_agreement"
  | "insurance"
  | "resume"
  | "other";

export const HR_DOC_TYPES: { value: HrDocType; label: string }[] = [
  { value: "background_check", label: "Background Check" },
  { value: "cpr_certification", label: "CPR Certification" },
  { value: "teaching_certificate", label: "Teaching Certificate" },
  { value: "contractor_agreement", label: "Signed Contractor Agreement" },
  { value: "insurance", label: "Insurance" },
  { value: "resume", label: "Resume" },
  { value: "other", label: "Other" },
];

export const HR_BUCKET = "instructor-hr-documents";

export type InstructorDocument = {
  id: string;
  instructor_id: string;
  doc_type: HrDocType;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_by: string | null;
  uploaded_at: string;
  expires_at: string | null;
  notes: string | null;
  supersedes_id: string | null;
  is_current: boolean;
  deleted_at: string | null;
};

async function logAccess(input: {
  instructor_id: string;
  document_id?: string | null;
  action: "view" | "download" | "upload" | "replace" | "delete";
  document_kind?: "hr" | "w9";
  metadata?: Record<string, unknown>;
}) {
  try {
    const { data: userData } = await supabase.auth.getUser();
    await (supabase.from as any)("instructor_document_access_log").insert({
      instructor_id: input.instructor_id,
      document_id: input.document_id ?? null,
      action: input.action,
      document_kind: input.document_kind ?? "hr",
      actor_id: userData?.user?.id ?? null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      metadata: input.metadata ?? {},
    });
  } catch {
    // Best-effort — never block the user action on audit write.
  }
}

export async function listInstructorDocuments(instructorId: string): Promise<InstructorDocument[]> {
  const { data, error } = await (supabase.from as any)("instructor_documents")
    .select("*")
    .eq("instructor_id", instructorId)
    .is("deleted_at", null)
    .order("uploaded_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as InstructorDocument[];
}

export async function uploadInstructorDocument(input: {
  instructorId: string;
  docType: HrDocType;
  file: File;
  expiresAt?: string | null;
  notes?: string | null;
  supersedesId?: string | null;
}): Promise<InstructorDocument> {
  const { instructorId, docType, file, expiresAt, notes, supersedesId } = input;
  const cleanName = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
  const path = `${instructorId}/${docType}/${Date.now()}-${cleanName}`;
  const up = await supabase.storage.from(HR_BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (up.error) throw up.error;

  const { data: userData } = await supabase.auth.getUser();
  const uploader = userData?.user?.id ?? null;

  // If replacing, mark the previous current version as superseded.
  if (supersedesId) {
    await (supabase.from as any)("instructor_documents")
      .update({ is_current: false })
      .eq("id", supersedesId);
  }

  const { data, error } = await (supabase.from as any)("instructor_documents")
    .insert({
      instructor_id: instructorId,
      doc_type: docType,
      storage_path: path,
      file_name: file.name,
      mime_type: file.type || null,
      size_bytes: file.size,
      uploaded_by: uploader,
      expires_at: expiresAt || null,
      notes: notes || null,
      supersedes_id: supersedesId || null,
      is_current: true,
    })
    .select("*")
    .single();
  if (error) throw error;

  await logAccess({
    instructor_id: instructorId,
    document_id: data.id,
    action: supersedesId ? "replace" : "upload",
    metadata: { doc_type: docType, file_name: file.name },
  });
  return data as InstructorDocument;
}

export async function getSignedDocumentUrl(doc: InstructorDocument, action: "view" | "download" = "download") {
  const { data, error } = await supabase.storage.from(HR_BUCKET).createSignedUrl(
    doc.storage_path,
    60,
    action === "download" ? { download: doc.file_name } : undefined,
  );
  if (error) throw error;
  await logAccess({
    instructor_id: doc.instructor_id,
    document_id: doc.id,
    action,
    metadata: { doc_type: doc.doc_type, file_name: doc.file_name },
  });
  return data.signedUrl;
}

export async function deleteInstructorDocument(doc: InstructorDocument): Promise<void> {
  // Soft-delete the row. Keep the object in storage for legal recovery.
  const { error } = await (supabase.from as any)("instructor_documents")
    .update({
      deleted_at: new Date().toISOString(),
      is_current: false,
    })
    .eq("id", doc.id);
  if (error) throw error;
  await logAccess({
    instructor_id: doc.instructor_id,
    document_id: doc.id,
    action: "delete",
    metadata: { doc_type: doc.doc_type, file_name: doc.file_name },
  });
}

export function docLabel(type: HrDocType): string {
  return HR_DOC_TYPES.find((t) => t.value === type)?.label ?? type;
}

export function expirationState(expires_at: string | null | undefined): {
  tone: "muted" | "amber" | "red" | "gray";
  label: string;
  daysUntil: number | null;
} {
  if (!expires_at) return { tone: "muted", label: "No expiration on file", daysUntil: null };
  const now = new Date();
  const exp = new Date(expires_at + "T00:00:00");
  const diff = Math.round((exp.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
  if (diff < 0) return { tone: "red", label: `Expired ${Math.abs(diff)} day${Math.abs(diff) === 1 ? "" : "s"} ago`, daysUntil: diff };
  if (diff <= 14) return { tone: "red", label: `Expires in ${diff} day${diff === 1 ? "" : "s"}`, daysUntil: diff };
  if (diff <= 60) return { tone: "amber", label: `Expires in ${diff} days`, daysUntil: diff };
  return { tone: "gray", label: `Expires ${exp.toLocaleDateString()}`, daysUntil: diff };
}
